/**
 * Agent-Kacheln — die typisierten, interaktiven Bausteine des agent-nativen Chats
 * (docs/KONZEPT-AGENT-NATIVE.md, Phase 0). Aus pages/assistent.tsx extrahiert, damit sowohl die
 * Fallback-Seite als auch die global angedockte Copilot-Spalte (ui/CopilotPanel) denselben
 * Renderer teilen. VERHALTENSNEUTRAL: identische Kachel-Typen, identisches Markup, identische
 * Aktions-Payloads. Aktionen auf einer Kachel gehen strukturiert an den Agenten zurück (die
 * Callbacks werden von der jeweiligen Session hereingereicht).
 */
import { useState } from "react";
import { Button, Card, Icon, Input, Select, Checkbox, Radio } from "./components";
import { ALLERGEN_LABEL } from "./lmiv";

// ---------------------------------------------------------------------------
// Kachel-Typen (Quelle: /api/agent/history + /api/agent/message|action)
// ---------------------------------------------------------------------------
export interface PreviewItem {
  name: string;
  description: string | null;
  priceDisplay: string | null;
  allergens: string[] | null;
  needsAllergen: boolean;
  hasSpell: boolean;
}
export interface PreviewSection {
  title: string;
  menuPriceDisplay: string | null;
  items: PreviewItem[];
}
export interface SpellItem {
  sectionIndex: number;
  itemIndex: number | null;
  field: "name" | "description" | "title";
  original: string;
  corrected: string;
  context: string;
}
export interface AllergenItem {
  sectionIndex: number;
  itemIndex: number;
  name: string;
  codes: string[];
  reason: string;
}
export interface ExistingCard {
  id: string;
  name: string;
}
export type CardOverride = { cardMode: "create" | "append" | "replace"; cardName?: string; cardId?: string };

export type AgentCard =
  | { type: "menu_preview"; importId: string; sourceLang: string; sections: PreviewSection[] }
  | { type: "spell_suggestions"; importId: string; suggestions: SpellItem[] }
  | { type: "allergen_confirm"; importId: string; positions: AllergenItem[] }
  | {
      type: "approval_gate";
      executionId: string;
      action: string;
      summary: string;
      risk: string;
      lmiv: boolean;
      existingCards?: ExistingCard[];
      cardOverride?: CardOverride;
    }
  // Aufgaben-Gedaechtnis: subtiler „Offenes Ziel"-Chip mit „Fortsetzen" (der Agent hat das
  // urspruengliche Ziel gemerkt, waehrend er durch eine Voraussetzung fuehrt).
  | { type: "pending_goal"; goal: string };

/** Callbacks, die eine Session (useAgentSession) in die Kacheln reinreicht. */
export interface CardCallbacks {
  onAction: (payload: Record<string, unknown>) => void;
  onApprove: (executionId: string, cardOverride?: CardOverride) => void;
  // S1.7/E4: Grund ist PFLICHT - ApprovalCard sammelt ihn (Auswahlliste + optionalem Freitext),
  // bevor sie das hier ueberhaupt aufruft.
  onReject: (executionId: string, reason: string) => void;
  /** Sendet eine Nachricht als Nutzer (z. B. „Fortsetzen" beim offenen Ziel). Optional. */
  onSend?: (text: string) => void;
}

// LMIV-Allergen-Codes -> Klartext (Anzeige). ALLERGEN_LABEL lebt zentral in ui/lmiv.
function allergenText(codes: string[] | null): string {
  if (codes === null) return "ungeprüft";
  if (codes.length === 0) return "keine";
  return codes.map((c) => `${c} ${ALLERGEN_LABEL[c] ?? ""}`.trim()).join(", ");
}

// ---------------------------------------------------------------------------
// Kachel-Renderer
// ---------------------------------------------------------------------------
export function CardView({
  card,
  busy,
  onAction,
  onApprove,
  onReject,
  onSend,
}: { card: AgentCard; busy: boolean } & CardCallbacks) {
  if (card.type === "menu_preview") return <MenuPreviewCard card={card} />;
  if (card.type === "spell_suggestions") return <SpellCard card={card} busy={busy} onAction={onAction} />;
  if (card.type === "allergen_confirm") return <AllergenCard card={card} busy={busy} onAction={onAction} />;
  if (card.type === "approval_gate") return <ApprovalCard card={card} busy={busy} onApprove={onApprove} onReject={onReject} />;
  if (card.type === "pending_goal") return <PendingGoalCard card={card} busy={busy} onSend={onSend} />;
  return null;
}

export function MenuPreviewCard({ card }: { card: Extract<AgentCard, { type: "menu_preview" }> }) {
  const itemCount = card.sections.reduce((n, s) => n + s.items.length, 0);
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-surface-variant px-4 py-3">
        <Icon name="restaurant_menu" className="text-[18px] text-primary" />
        <span className="flex-1 text-body-md font-semibold text-on-background">Erkannte Speisekarte</span>
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-label-md font-semibold text-primary">{card.sections.length} Abschnitte · {itemCount} Positionen</span>
      </div>
      <div className="max-h-[420px] overflow-y-auto px-4 py-3">
        {card.sections.map((sec, si) => (
          <div key={si} className="mb-4 last:mb-0">
            <div className="mb-2 text-label-md font-semibold uppercase tracking-wide text-on-surface-variant">
              {sec.title}{sec.menuPriceDisplay ? ` · ${sec.menuPriceDisplay}` : ""}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {sec.items.map((it, ii) => (
                <div key={ii} className="rounded-[11px] border border-surface-variant bg-surface p-2.5 dark:bg-night-canvas">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-body-sm font-semibold text-on-background">{it.name}</div>
                      {it.description && <div className="mt-0.5 line-clamp-2 text-label-md text-on-surface-variant">{it.description}</div>}
                    </div>
                    {it.priceDisplay && <div className="shrink-0 text-body-sm font-semibold text-on-background">{it.priceDisplay}</div>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {it.hasSpell && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-primary">Rechtschreibung</span>}
                    {it.needsAllergen && <span className="rounded-full bg-[#F0EAE0] px-2 py-0.5 text-[10.5px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">Allergene prüfen</span>}
                    {it.allergens && it.allergens.length > 0 && <span className="text-[10.5px] text-on-surface-variant">{allergenText(it.allergens)}</span>}
                    {it.allergens !== null && it.allergens.length === 0 && <span className="text-[10.5px] text-on-surface-variant">keine Allergene</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function SpellCard({ card, busy, onAction }: { card: Extract<AgentCard, { type: "spell_suggestions" }>; busy: boolean; onAction: (p: Record<string, unknown>) => void }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-surface-variant px-4 py-3">
        <Icon name="spellcheck" className="text-[18px] text-primary" />
        <span className="flex-1 text-body-md font-semibold text-on-background">Rechtschreibung</span>
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-label-md font-semibold text-primary">{card.suggestions.length} Vorschläge</span>
      </div>
      <div className="flex flex-col gap-2 px-4 py-3">
        {card.suggestions.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-body-sm">
            <span className="text-on-surface-variant line-through">{s.original}</span>
            <span className="text-on-surface-variant">→</span>
            <span className="font-semibold text-primary">{s.corrected}</span>
            {s.context && <span className="truncate text-label-md text-on-surface-variant">· {s.context}</span>}
            <Button variant="ghost" className="ml-auto shrink-0 px-2 py-1 text-label-md" disabled={busy}
              onClick={() => onAction({ action: "accept_spelling", sectionIndex: s.sectionIndex, itemIndex: s.itemIndex, field: s.field })}>
              Übernehmen
            </Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 border-t border-surface-variant px-4 py-3">
        <Button disabled={busy} onClick={() => onAction({ action: "accept_all_spelling" })}>Alle übernehmen</Button>
      </div>
    </Card>
  );
}

export function AllergenCard({ card, busy, onAction }: { card: Extract<AgentCard, { type: "allergen_confirm" }>; busy: boolean; onAction: (p: Record<string, unknown>) => void }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-surface-variant px-4 py-3">
        <Icon name="warning" className="text-[18px] text-secondary dark:text-[#b8ae9e]" />
        <span className="flex-1 text-body-md font-semibold text-on-background">Allergene</span>
        <span className="rounded-full bg-[#F0EAE0] px-2.5 py-1 text-label-md font-semibold text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">{card.positions.length} zu bestätigen</span>
      </div>
      <div className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-2">
        {card.positions.map((p, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-secondary" />
            <div className="min-w-0 flex-1">
              <div className="text-body-sm font-semibold text-on-background">{p.name}</div>
              <div className="mt-0.5 text-label-md text-on-surface-variant">{p.reason}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <Button className="px-2.5 py-1.5 text-label-md" disabled={busy}
                  onClick={() => onAction({ action: "confirm_allergen", sectionIndex: p.sectionIndex, itemIndex: p.itemIndex, codes: p.codes })}>
                  {p.codes.map((c) => c).join("+")} bestätigen ({p.codes.map((c) => ALLERGEN_LABEL[c] ?? c).join(", ")})
                </Button>
                <Button variant="ghost" className="px-2 py-1 text-label-md" disabled={busy}
                  onClick={() => onAction({ action: "no_allergen", sectionIndex: p.sectionIndex, itemIndex: p.itemIndex })}>
                  keine
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 border-t border-surface-variant px-4 py-3">
        <Button disabled={busy} onClick={() => onAction({ action: "confirm_all_allergen" })}>Alle Vorschläge bestätigen</Button>
      </div>
    </Card>
  );
}

/**
 * Freigabe-Aktionen -> Button-Beschriftung. Deckt die bekannten Actions ab; unbekannte
 * approval-Actions bekommen ein neutrales „Freigeben" (nie ein irreführend beschrifteter Button).
 */
const APPROVE_LABEL: Record<string, string> = {
  commit_card: "Karte übernehmen",
  send_offer: "Angebot senden",
  places_lookup: "Abrufen (Google Places)",
  apply_enrichment: "Übernehmen",
  apply_menu_spelling: "Rechtschreibung übernehmen",
  knowledge_write: "Wissen speichern",
};
/** Freigabe-Aktionen -> Kopf-Icon (Material Symbols); Fallback „task_alt" für Unbekanntes. */
const APPROVE_ICON: Record<string, string> = {
  commit_card: "menu_book",
  send_offer: "outgoing_mail",
  places_lookup: "travel_explore",
  apply_enrichment: "person_check",
  apply_menu_spelling: "spellcheck",
  knowledge_write: "school", // „gelerntes" Wissen; bewusst nicht menu_book (das nutzt commit_card)
};

// E4 (Antoines Festlegung, docs/LOOP-GEDAECHTNIS.md § 3): "Pflicht, als Auswahlliste plus
// optionalem Freitext" - ein Klick statt Tippzwang, ohne Lernsignal zu verlieren. Generisch
// gehalten, weil ALLE 58 durable-pause-Werkzeuge dieselbe Ablehnung durchlaufen (apps/agent/src/
// maestro-agent.ts reject()) - keine werkzeugspezifische Liste.
const ABLEHN_GRUENDE = [
  { value: "falsche_angaben", label: "Falsche Angaben (Betrag, Text, Empfänger, Menge …)" },
  { value: "zu_frueh", label: "Zu früh — noch nicht bereit" },
  { value: "nicht_noetig", label: "Nicht nötig" },
  { value: "sonstiges", label: "Sonstiges" },
] as const;

export function ApprovalCard({ card, busy, onApprove, onReject }: {
  card: Extract<AgentCard, { type: "approval_gate" }>;
  busy: boolean;
  onApprove: (executionId: string, ov?: CardOverride) => void;
  // S1.7/E4: Grund ist PFLICHT - ApprovalCard sammelt ihn (Auswahlliste + optionalem Freitext),
  // bevor sie das hier ueberhaupt aufruft.
  onReject: (executionId: string, reason: string) => void;
}) {
  const isCommit = card.action === "commit_card";
  const approveLabel = APPROVE_LABEL[card.action] ?? "Freigeben";
  const headerIcon = APPROVE_ICON[card.action] ?? "task_alt";
  const existing = card.existingCards ?? [];
  const [mode, setMode] = useState<"create" | "append" | "replace">(card.cardOverride?.cardMode ?? "create");
  const [cardName, setCardName] = useState(card.cardOverride?.cardName ?? "");
  const [cardId, setCardId] = useState(card.cardOverride?.cardId ?? existing[0]?.id ?? "");
  const [lmiv, setLmiv] = useState(false);
  const [ablehnenOffen, setAblehnenOffen] = useState(false);
  const [ablehnGrund, setAblehnGrund] = useState<(typeof ABLEHN_GRUENDE)[number]["value"] | "">("");
  const [ablehnFreitext, setAblehnFreitext] = useState("");

  function ablehnungBestaetigen() {
    const label = ABLEHN_GRUENDE.find((g) => g.value === ablehnGrund)?.label ?? "";
    const reason = ablehnFreitext.trim() ? `${label} — ${ablehnFreitext.trim()}` : label;
    onReject(card.executionId, reason);
  }

  const canApprove = isCommit ? lmiv && (mode === "create" ? cardName.trim().length > 0 : cardId.length > 0) : true;
  const ov: CardOverride | undefined = isCommit ? (mode === "create" ? { cardMode: "create", cardName: cardName.trim() || "Neue Karte" } : { cardMode: mode, cardId }) : undefined;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-surface-variant px-4 py-3">
        <Icon name={headerIcon} className="text-[18px] text-primary" />
        <span className="flex-1 text-body-md font-semibold text-on-background">{card.summary}</span>
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-label-md font-semibold text-primary">Freigabe nötig</span>
      </div>
      <div className="flex flex-col gap-3 px-4 py-3">
        {isCommit && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-3 text-body-sm">
              <label className="flex items-center gap-1.5"><Radio checked={mode === "create"} onChange={() => setMode("create")} /> Neue Karte</label>
              <label className={`flex items-center gap-1.5 ${existing.length === 0 ? "opacity-40" : ""}`}><Radio disabled={existing.length === 0} checked={mode === "append"} onChange={() => setMode("append")} /> Anhängen</label>
              <label className={`flex items-center gap-1.5 ${existing.length === 0 ? "opacity-40" : ""}`}><Radio disabled={existing.length === 0} checked={mode === "replace"} onChange={() => setMode("replace")} /> Ersetzen</label>
            </div>
            {mode === "create" ? (
              <Input placeholder="Name der neuen Karte (z. B. Sommerkarte 2026)" value={cardName} onChange={(e) => setCardName(e.target.value)} />
            ) : (
              <Select value={cardId} onValueChange={setCardId} options={existing.map((c) => ({ value: c.id, label: c.name }))} />
            )}
            <label className="flex cursor-pointer items-start gap-2.5 text-body-sm text-on-surface-variant">
              <Checkbox checked={lmiv} onChange={(e) => setLmiv(e.target.checked)} className="mt-0.5" />
              <span>Ich bestätige, dass alle Angaben zu Zutaten und Allergenen korrekt sind (LMIV).</span>
            </label>
          </div>
        )}
        {card.risk === "medium" && (
          <p className="flex items-start gap-1.5 text-label-md text-secondary dark:text-[#b8ae9e]">
            <Icon name="info" className="mt-px text-[15px]" />
            <span>
              {card.action === "places_lookup"
                ? "Kostenpflichtig — dieser Abruf bei Google Places verursacht Kosten."
                : "Mittleres Risiko — bitte vor der Freigabe prüfen."}
            </span>
          </p>
        )}
        {ablehnenOffen ? (
          <div className="flex flex-col gap-2 rounded-xl border border-outline-variant/40 bg-surface-container-low p-3 dark:border-white/10 dark:bg-white/[0.03]">
            <span className="text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Warum ablehnen?</span>
            <Select
              value={ablehnGrund}
              onValueChange={(v) => setAblehnGrund(v as typeof ablehnGrund)}
              options={ABLEHN_GRUENDE.map((g) => ({ value: g.value, label: g.label }))}
              placeholder="Grund wählen …"
            />
            <Input placeholder="Weitere Details (optional)" value={ablehnFreitext} onChange={(e) => setAblehnFreitext(e.target.value)} />
            <div className="flex items-center gap-2">
              <Button variant="secondary" disabled={busy} onClick={() => setAblehnenOffen(false)}>Abbrechen</Button>
              <Button disabled={busy || !ablehnGrund} onClick={ablehnungBestaetigen}>Ablehnung bestätigen</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button disabled={busy || !canApprove} onClick={() => onApprove(card.executionId, ov)}>
              {approveLabel}
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => setAblehnenOffen(true)}>Ablehnen</Button>
          </div>
        )}
        <p className="text-body-sm text-on-surface-variant">Vorher passiert nichts — erst mit deiner Freigabe wird es ausgeführt.</p>
      </div>
    </Card>
  );
}

/**
 * „Offenes Ziel"-Chip (Aufgaben-Gedächtnis): subtil und nicht-intrusiv. Zeigt das vom Agenten
 * gemerkte ursprüngliche Ziel, während er durch eine fehlende Voraussetzung führt. „Fortsetzen"
 * schickt „Weiter mit: <Ziel>" als Nutzer-Nachricht zurück — der Agent nimmt das Ziel wieder auf.
 */
export function PendingGoalCard({ card, busy, onSend }: { card: Extract<AgentCard, { type: "pending_goal" }>; busy: boolean; onSend?: (text: string) => void }) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-surface-variant bg-surface px-3 py-1.5 text-body-sm dark:bg-night-canvas">
      <Icon name="flag" className="shrink-0 text-[16px] text-primary" />
      <span className="min-w-0 flex-1 truncate text-on-surface-variant">
        Offenes Ziel: <span className="font-semibold text-on-background">{card.goal}</span>
      </span>
      {onSend && (
        <Button
          variant="ghost"
          className="shrink-0 rounded-full px-2.5 py-1 text-label-md"
          disabled={busy}
          onClick={() => onSend(`Weiter mit: ${card.goal}`)}
        >
          Fortsetzen
        </Button>
      )}
    </div>
  );
}
